import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, ChevronUp, ChevronDown, Music } from 'lucide-react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';

export const GlobalAudioPlayer = () => {
  const {
    song,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    isMinimized,
    hasInteracted,
    togglePlay,
    seek,
    setVolumeLevel,
    toggleMute,
    toggleMinimize
  } = useAudioPlayer();

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  const handleProgressChange = (e) => {
    const newTime = (e.target.value / 100) * duration;
    seek(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolumeLevel(newVolume);
  };

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, delay: 1 }}
      className="fixed bottom-0 left-0 right-0 z-50"
      data-testid="global-audio-player"
    >
      <AnimatePresence mode="wait">
        {isMinimized ? (
          /* Minimized Player */
          <motion.div
            key="minimized"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="bg-gradient-to-r from-[#1A1412] via-[#2C221F] to-[#1A1412] border-t border-[#D97736]/30"
          >
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={togglePlay}
                  className="w-10 h-10 rounded-full bg-[#D97736] hover:bg-[#F08F4D] flex items-center justify-center transition-colors"
                  data-testid="mini-play-button"
                >
                  {isPlaying ? (
                    <Pause className="text-white" size={18} fill="white" />
                  ) : (
                    <Play className="text-white ml-0.5" size={18} fill="white" />
                  )}
                </button>
                <div className="hidden sm:block">
                  <p className="text-[#FDFBF7] text-sm font-semibold">{song.title}</p>
                  <p className="text-[#CFB53B] text-xs">{song.artist}</p>
                </div>
              </div>
              
              {/* Mini Progress */}
              <div className="flex-1 mx-4 hidden md:block">
                <div className="flex items-center gap-2">
                  <span className="text-[#A89B95] text-xs">{formatTime(currentTime)}</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleProgressChange}
                    className="flex-1 h-1 bg-[#3D302C] rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #D97736 0%, #D97736 ${progress}%, #3D302C ${progress}%, #3D302C 100%)`
                    }}
                  />
                  <span className="text-[#A89B95] text-xs">{formatTime(duration)}</span>
                </div>
              </div>

              <button
                onClick={toggleMinimize}
                className="p-2 text-[#D97736] hover:text-[#F08F4D] transition-colors"
              >
                <ChevronUp size={20} />
              </button>
            </div>
          </motion.div>
        ) : (
          /* Expanded Player */
          <motion.div
            key="expanded"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="bg-gradient-to-r from-[#1A1412] via-[#2C221F] to-[#1A1412] border-t border-[#D97736]/30 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.5)]"
          >
            <div className="max-w-4xl mx-auto px-4 py-4">
              <div className="flex items-center gap-4">
                {/* Vinyl Animation */}
                <motion.div
                  animate={{ rotate: isPlaying ? 360 : 0 }}
                  transition={{ duration: 3, repeat: isPlaying ? Infinity : 0, ease: "linear" }}
                  className="flex-shrink-0 hidden sm:block"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1A1412] to-[#2C221F] border-2 border-[#D97736] flex items-center justify-center relative">
                    <div className="w-6 h-6 rounded-full bg-[#CFB53B]"></div>
                    <div className="absolute inset-0 rounded-full" style={{
                      background: 'repeating-conic-gradient(from 0deg, transparent 0deg 10deg, rgba(217, 119, 54, 0.1) 10deg 20deg)'
                    }}></div>
                  </div>
                </motion.div>

                {/* Main Controls */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#FDFBF7]">
                        {song.title}
                      </h3>
                      <p className="text-[#CFB53B] text-sm">{song.artist}</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Play/Pause */}
                      <button
                        onClick={togglePlay}
                        className="w-12 h-12 rounded-full bg-[#D97736] hover:bg-[#F08F4D] flex items-center justify-center transition-colors shadow-lg"
                        data-testid="expanded-play-button"
                      >
                        {isPlaying ? (
                          <Pause className="text-white" size={22} fill="white" />
                        ) : (
                          <Play className="text-white ml-1" size={22} fill="white" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={handleProgressChange}
                      className="w-full h-2 bg-[#3D302C] rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #D97736 0%, #D97736 ${progress}%, #3D302C ${progress}%, #3D302C 100%)`
                      }}
                      data-testid="global-progress-bar"
                    />
                    <div className="flex justify-between text-xs text-[#A89B95] mt-1">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>

                  {/* Volume + Minimize */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={toggleMute}
                        className="text-[#D97736] hover:text-[#F08F4D] transition-colors"
                        data-testid="global-mute-button"
                      >
                        {isMuted || volume === 0 ? (
                          <VolumeX size={18} />
                        ) : (
                          <Volume2 size={18} />
                        )}
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={isMuted ? 0 : volume * 100}
                        onChange={handleVolumeChange}
                        className="w-20 h-1 bg-[#3D302C] rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #D97736 0%, #D97736 ${isMuted ? 0 : volume * 100}%, #3D302C ${isMuted ? 0 : volume * 100}%, #3D302C 100%)`
                        }}
                        data-testid="global-volume-slider"
                      />
                    </div>

                    {/* Waveform Animation */}
                    <div className="hidden md:flex items-center gap-1">
                      {[...Array(6)].map((_, i) => (
                        <motion.div
                          key={i}
                          animate={{
                            scaleY: isPlaying ? [0.3, 1, 0.3] : 0.3
                          }}
                          transition={{
                            duration: 0.6,
                            repeat: isPlaying ? Infinity : 0,
                            delay: i * 0.1
                          }}
                          className="w-1 h-6 bg-[#CFB53B] rounded-full origin-center"
                        />
                      ))}
                    </div>

                    <button
                      onClick={toggleMinimize}
                      className="p-2 text-[#D97736] hover:text-[#F08F4D] transition-colors"
                    >
                      <ChevronDown size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
